import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';
import { useSession } from '../../auth/useSession.js';
import { ApiError } from '../../lib/apiFetch.js';
import type { TimesheetGridRow } from './contractsMirror.js';
import { useEntryMutation } from './useApprovalMutation.js';

/**
 * Record a shift that was never punched — the supervisor's correction path when someone forgets to
 * clock in, or works somewhere with no signal. Gated on `time.approve`, matching the endpoint.
 *
 * The SPA sends the raw clock-in/clock-out span and nothing else: the SERVER derives the hours and
 * applies the lunch rule (D-03 / T-22-payroll). A shift entered here is deliberately marked by the
 * times it carries, not by an hours figure typed straight in, so payroll math has one owner.
 *
 * Collapsed by default. It opens on click — the content inside is plain markup, never hidden behind
 * an animation, so it cannot fail to appear.
 */
export interface AddEntryProps {
  rows: TimesheetGridRow[];
  dates: string[];
}

export function AddEntry({ rows, dates }: AddEntryProps) {
  const { t } = useTranslation();
  const { session } = useSession();
  const canManage = Boolean(session?.user?.permissions?.includes('time.approve'));

  const entry = useEntryMutation();
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [date, setDate] = useState('');
  const [clockInLocal, setClockInLocal] = useState('');
  const [clockOutLocal, setClockOutLocal] = useState('');

  if (!canManage || rows.length === 0) return null;

  const errorMessage =
    entry.error instanceof ApiError
      ? t(`timesheets:addEntry.errors.${entry.error.message}`, {
          defaultValue: t('timesheets:addEntry.errors.generic'),
        })
      : entry.error
        ? t('timesheets:addEntry.errors.generic')
        : null;

  const complete = employeeId && date && clockInLocal && clockOutLocal;

  function reset() {
    setEmployeeId('');
    setDate('');
    setClockInLocal('');
    setClockOutLocal('');
    entry.reset();
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!complete) return;
    entry.mutate(
      {
        employeeId: Number(employeeId),
        date,
        // datetime-local is wall-clock; the server stores and compares instants.
        clockInAt: new Date(clockInLocal).toISOString(),
        clockOutAt: new Date(clockOutLocal).toISOString(),
      },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
      },
    );
  }

  return (
    <section
      aria-label={t('timesheets:addEntry.regionLabel')}
      className="mb-4 rounded-md border border-border bg-card p-4"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-semibold text-foreground text-sm">{t('timesheets:addEntry.heading')}</h2>
          <p className="mt-1 text-muted-foreground text-sm">{t('timesheets:addEntry.body')}</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="ml-auto"
          aria-expanded={open}
          onClick={() => {
            if (open) reset();
            setOpen((prev) => !prev);
          }}
        >
          {open ? t('timesheets:addEntry.close') : t('timesheets:addEntry.action')}
        </Button>
      </div>

      {open ? (
        <form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={submit}>
          <Field id="add-entry-employee" label={t('timesheets:addEntry.employeeLabel')}>
            <select
              id="add-entry-employee"
              className={CONTROL}
              value={employeeId}
              onChange={(event) => setEmployeeId(event.target.value)}
              required
            >
              <option value="">{t('timesheets:addEntry.employeePlaceholder')}</option>
              {rows.map((row) => (
                <option key={row.employeeId} value={row.employeeId}>
                  {row.employeeName}
                </option>
              ))}
            </select>
          </Field>

          <Field id="add-entry-date" label={t('timesheets:addEntry.dateLabel')}>
            <select
              id="add-entry-date"
              className={CONTROL}
              value={date}
              onChange={(event) => setDate(event.target.value)}
              required
            >
              <option value="">{t('timesheets:addEntry.datePlaceholder')}</option>
              {dates.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </Field>

          <Field id="add-entry-in" label={t('timesheets:addEntry.clockInLabel')}>
            <input
              id="add-entry-in"
              type="datetime-local"
              className={CONTROL}
              value={clockInLocal}
              onChange={(event) => setClockInLocal(event.target.value)}
              required
            />
          </Field>

          <Field id="add-entry-out" label={t('timesheets:addEntry.clockOutLabel')}>
            <input
              id="add-entry-out"
              type="datetime-local"
              className={CONTROL}
              value={clockOutLocal}
              onChange={(event) => setClockOutLocal(event.target.value)}
              required
            />
          </Field>

          <Button type="submit" size="sm" disabled={entry.isPending || !complete}>
            {t('timesheets:addEntry.submit')}
          </Button>

          {errorMessage ? (
            <p role="alert" aria-live="assertive" className="w-full text-destructive text-sm">
              {errorMessage}
            </p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}

/** The one control treatment on this screen, matching the resolve-open form. */
const CONTROL =
  'mt-1 h-10 w-full min-w-0 rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    // Sized by the flex row, not by its control: a <select> of real job names is
    // far wider than a phone, and a flex item's default `min-width: auto` would
    // hand that intrinsic width straight to the page.
    <div className="w-full min-w-0 sm:w-auto">
      <label htmlFor={id} className="font-medium text-foreground text-xs">
        {label}
      </label>
      {children}
    </div>
  );
}
